// Empire (P33, step 16d): totals over every owned system, then one card per system with resources,
// running work and stationed ships; a card opens its system.
import type { FullSystem, OwnFleet, ShipCounts } from '@stars-and-ships/api-types';
import { Link, useOutletContext } from 'react-router';
import { useFleets, useSystems } from '../api/queries';
import type { GameContext } from '../components/GameLayout';
import { ErrorNotice, Page, Panel, ResourceChip, Spinner, Stat } from '../components/ui';
import { fleetsInFlight, ownShipTotals, runningWork, shipsAt, sumResources } from '../empire';
import {
  FACILITY_LABELS,
  formatDuration,
  formatNumber,
  formatShips,
  RESOURCE_KEYS,
  SHIP_LABELS,
} from '../labels';
import { liveResources, useNow } from '../state/live';

export function EmpireScreen() {
  const { galaxyId, me } = useOutletContext<GameContext>();
  const owned = me?.systems ?? [];
  const results = useSystems(
    galaxyId,
    owned.map((s) => s.id)
  );
  const fleets = useFleets(galaxyId);
  const now = useNow();
  if (!me) return <Spinner />;

  const systems = results.flatMap((r) => (r.data?.tier === 'full' ? [r.data.system] : []));
  const ownFleets = fleets.data?.items ?? [];
  const loading = results.some((r) => r.isPending) || fleets.isPending;
  return (
    <Page width="wide">
      <h1 className="font-display text-2xl font-bold uppercase tracking-[0.08em] text-text">
        Empire
      </h1>
      {fleets.error ? <ErrorNotice error={fleets.error} /> : null}
      <Totals
        systems={systems}
        fleets={ownFleets}
        credits={me.credits}
        systemCount={owned.length}
        loading={loading}
        now={now}
      />
      <ul className="grid gap-3 lg:grid-cols-2">
        {owned.map((s, index) => {
          const result = results[index];
          const full = result?.data?.tier === 'full' ? result.data.system : undefined;
          return (
            <li key={s.id}>
              <Link
                to={`/g/${galaxyId}/systems/${s.id}`}
                data-empire-system={s.name}
                className="block hud-cut-sm border border-line bg-panel p-4 hover:border-accent"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="min-w-0 truncate font-semibold text-text">{s.name}</span>
                  <span className="shrink-0 text-xs text-muted">
                    {index === 0 ? 'Home system' : 'Colony'}
                    {full ? ` · ${formatNumber(full.points)} points` : ''}
                  </span>
                </div>
                {full ? (
                  <SystemDetails system={full} ships={shipsAt(ownFleets, s.id)} now={now} />
                ) : result?.error ? (
                  <ErrorNotice error={result.error} />
                ) : (
                  <Spinner />
                )}
              </Link>
            </li>
          );
        })}
      </ul>
    </Page>
  );
}

function Totals({
  systems,
  fleets,
  credits,
  systemCount,
  loading,
  now,
}: {
  systems: FullSystem[];
  fleets: OwnFleet[];
  credits: number;
  systemCount: number;
  loading: boolean;
  now: number;
}) {
  const amounts = sumResources(systems.map((s) => liveResources(s.resources, now)));
  const caps = sumResources(systems.map((s) => s.resources.caps));
  const rates = sumResources(systems.map((s) => s.resources.ratesPerHour));
  const points = systems.reduce((sum, s) => sum + s.points, 0);
  const pending = (value: number) => (loading ? '—' : formatNumber(value));
  return (
    <Panel title="Totals">
      <div className="flex gap-2">
        {RESOURCE_KEYS.map((key) => (
          <ResourceChip
            key={key}
            resource={key}
            amount={loading ? undefined : amounts[key]}
            {...(loading ? {} : { cap: caps[key], ratePerHour: rates[key] })}
          />
        ))}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4" data-empire-totals>
        <Stat label="Credits" value={formatNumber(credits)} />
        <Stat label="Points" value={<span data-points>{pending(points)}</span>} />
        <Stat label="Systems" value={formatNumber(systemCount)} />
        <Stat label="Fleets in flight" value={pending(fleetsInFlight(fleets))} />
      </div>
      <ShipLine label="Ships" ships={loading ? undefined : ownShipTotals(fleets)} />
    </Panel>
  );
}

function SystemDetails({
  system,
  ships,
  now,
}: {
  system: FullSystem;
  ships: ShipCounts;
  now: number;
}) {
  const live = liveResources(system.resources, now);
  const work = runningWork(system);
  const left = (iso: string | null) => {
    const end = iso ? Date.parse(iso) : null;
    return end === null ? 'waiting' : end > now ? formatDuration(end - now) : 'finishing…';
  };
  const waiting = (n: number) => (n > 0 ? ` · +${n} waiting` : '');
  return (
    <div className="mt-3 space-y-3">
      <div className="flex gap-2">
        {RESOURCE_KEYS.map((key) => (
          <ResourceChip
            key={key}
            resource={key}
            amount={live[key]}
            cap={system.resources.caps[key]}
          />
        ))}
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
        <WorkLine label="Building" data="build">
          {work.build
            ? `${FACILITY_LABELS[work.build.item.facilityType]} → Lv ${work.build.item.targetLevel} · ${left(work.build.item.completesAt)}${waiting(work.build.waiting)}`
            : null}
        </WorkLine>
        <WorkLine label="Trade" data="trade">
          {work.trade
            ? `+${formatNumber(work.trade.creditsOut)} Credits · ${left(work.trade.completesAt)}`
            : null}
        </WorkLine>
        <WorkLine label="Shipyard" data="production">
          {work.production
            ? `${SHIP_LABELS[work.production.item.shipType]} ${formatNumber(work.production.item.delivered)}/${formatNumber(work.production.item.quantity)} · ${work.production.item.nextShipAt ? `next in ${left(work.production.item.nextShipAt)}` : 'waiting'}${waiting(work.production.waiting)}`
            : null}
        </WorkLine>
      </dl>
      <ShipLine label="Stationed" ships={ships} />
    </div>
  );
}

function WorkLine({
  label,
  data,
  children,
}: {
  label: string;
  data: string;
  children: string | null;
}) {
  return (
    <>
      <dt className="font-display text-[10px] font-bold uppercase leading-5 tracking-[0.16em] text-faint">
        {label}
      </dt>
      <dd
        data-empire-work={data}
        className={`min-w-0 truncate font-mono text-xs leading-5 tabular-nums ${children ? 'text-info' : 'text-faint'}`}
      >
        {children ?? 'Idle'}
      </dd>
    </>
  );
}

function ShipLine({ label, ships }: { label: string; ships: ShipCounts | undefined }) {
  return (
    <p className="mt-3 text-sm">
      <span className="font-display text-[10px] font-bold uppercase tracking-[0.16em] text-faint">
        {label}
      </span>{' '}
      <span className="text-text" data-empire-ships={label}>
        {ships === undefined ? '—' : formatShips(ships)}
      </span>
    </p>
  );
}
